import {
  DashboardType,
  DataVersionStatus,
  DATE_FORMAT,
  getDayjs,
  ISODate,
  useSystemClassifierItemsBy,
  ValidationDashboardDataObjectVersionType,
  ValidationDashboardListDto,
  VersionDisplay,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { memo } from 'react';
import translations from './UsedDataCell.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useDashboardType } from './useDashboardType';

const { addTranslations, t } = i18nNsFactory(
  'validation-dashboards-table-used-data-cell',
);
addTranslations(translations);

export const UsedDataCell = memo(
  ({ data }: { data: ValidationDashboardListDto }) => {
    const {
      dashboardTypeId,
      valueDateFrom,
      valueDateTo,
      versionSelectionRuleId,
      version1SelectionRuleId,
      version2SelectionRuleId,
    } = data;

    const classifiers = useSystemClassifierItemsBy([
      'TIME_SERIES_VERSION',
      'TIME_SERIES_COMPARISON_VERSION',
    ]);
    const type = useDashboardType(dashboardTypeId);

    const version1 = data.versions?.[0];

    const versionSelectionRuleName = versionSelectionRuleId
      ? (classifiers['TIME_SERIES_VERSION'].get(versionSelectionRuleId)?.name ??
        undefined)
      : undefined;

    const version1SelectionRuleName = version1SelectionRuleId
      ? (classifiers['TIME_SERIES_COMPARISON_VERSION'].get(
          version1SelectionRuleId,
        )?.name ?? undefined)
      : undefined;

    const version2SelectionRuleName = version2SelectionRuleId
      ? (classifiers['TIME_SERIES_COMPARISON_VERSION'].get(
          version2SelectionRuleId,
        )?.name ?? undefined)
      : undefined;

    switch (type) {
      case DashboardType.COMPARISON:
        return <ComparisonUsedDataCell versions={data.versions} />;

      case DashboardType.TIME_SERIES:
      case DashboardType.HODRICK_PRESCOTT:
      case DashboardType.OUTLIERS:
        return (
          <TimeSeriesUsedDataCell
            valueDateFrom={valueDateFrom}
            valueDateTo={valueDateTo}
            versionSelectionRuleName={versionSelectionRuleName}
          />
        );

      case DashboardType.TIME_SERIES_COMPARISON:
      case DashboardType.HODRICK_PRESCOTT_COMPARISON:
      case DashboardType.OUTLIERS_COMPARISON:
        return (
          <TimeSeriesComparisonUsedDataCell
            valueDateFrom={valueDateFrom}
            valueDateTo={valueDateTo}
            version1SelectionRuleName={version1SelectionRuleName}
            version2SelectionRuleName={version2SelectionRuleName}
          />
        );

      default:
        return <CrossTableUsedDataCell version={version1} />;
    }
  },
);

const ComparisonUsedDataCell = memo(
  ({ versions }: { versions?: ValidationDashboardDataObjectVersionType[] }) => {
    const version1 = versions?.[0];
    const version2 = versions?.[1];

    const renderComparisonVersion = (
      version: ValidationDashboardDataObjectVersionType,
    ) => {
      const { dataObjectVersion, sequenceNumber } = version;
      const { valueDate, versionNumber, minorNumber, statusCode } =
        dataObjectVersion;

      return (
        <Flex gap="small">
          <span>
            <strong>{t('label.comparative', { sequenceNumber })}:</strong>
          </span>
          <span>{getDayjs(valueDate)?.format(DATE_FORMAT)}</span>
          <VersionDisplay
            versionNumber={versionNumber}
            minorNumber={minorNumber}
            status={statusCode as DataVersionStatus}
          >
            <DataVersionStatus status={statusCode as DataVersionStatus} />
          </VersionDisplay>
        </Flex>
      );
    };

    if (!version1 || !version2) return null;

    return (
      <Flex gap="small" vertical>
        {renderComparisonVersion(version1)}
        {renderComparisonVersion(version2)}
      </Flex>
    );
  },
);

const TimeSeriesUsedDataCell = memo(
  ({
    valueDateFrom,
    valueDateTo,
    versionSelectionRuleName,
  }: {
    valueDateFrom?: ISODate | null;
    valueDateTo?: ISODate | null;
    versionSelectionRuleName?: string;
  }) => {
    if (!valueDateFrom || !valueDateTo || !versionSelectionRuleName)
      return null;

    return (
      <span>
        {getDayjs(valueDateFrom)?.format(DATE_FORMAT)} -{' '}
        {getDayjs(valueDateTo)?.format(DATE_FORMAT)} -{' '}
        {versionSelectionRuleName}
      </span>
    );
  },
);

const TimeSeriesComparisonUsedDataCell = memo(
  ({
    valueDateFrom,
    valueDateTo,
    version1SelectionRuleName,
    version2SelectionRuleName,
  }: {
    valueDateFrom?: ISODate | null;
    valueDateTo?: ISODate | null;
    version1SelectionRuleName?: string;
    version2SelectionRuleName?: string;
  }) => {
    if (
      !valueDateFrom ||
      !valueDateTo ||
      !version1SelectionRuleName ||
      !version2SelectionRuleName
    )
      return null;

    return (
      <Flex vertical gap="small">
        <span>
          <strong>{t('label.valueDateRange')}: </strong>
          {getDayjs(valueDateFrom)?.format(DATE_FORMAT)} -{' '}
          {getDayjs(valueDateTo)?.format(DATE_FORMAT)}
        </span>
        <span>
          <strong>{t('label.version1SelectionRule')}: </strong>
          {version1SelectionRuleName}
        </span>
        <span>
          <strong>{t('label.version2SelectionRule')}: </strong>
          {version2SelectionRuleName}
        </span>
      </Flex>
    );
  },
);

const CrossTableUsedDataCell = memo(
  ({ version }: { version?: ValidationDashboardDataObjectVersionType }) => {
    if (!version) return null;

    const { dataObjectVersion } = version;
    const { valueDate, versionNumber, minorNumber, statusCode } =
      dataObjectVersion;

    return (
      <Flex gap="small">
        <span>{getDayjs(valueDate)?.format(DATE_FORMAT)}</span>
        <VersionDisplay
          versionNumber={versionNumber}
          minorNumber={minorNumber}
          status={statusCode as DataVersionStatus}
        >
          <DataVersionStatus status={statusCode as DataVersionStatus} />
        </VersionDisplay>
      </Flex>
    );
  },
);
